import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import useFieldDashboard from "@/lib/fieldDashboard/useFieldDashboard";
import useRequest from "@/lib/request/useRequest";
import useMap from "@/lib/map/useMap";
import useConfirm from "@/lib/confirm/useConfirm";
import { useTranslation } from "react-i18next";
import { useI18n } from "@/lib/i18n/useI18n";
import { IrrigationOperation, ListIrrigations } from "@/types/operations";
import { useCallback, useMemo, useState, useEffect } from "react";
import { ColumnDef, SortingState } from "@tanstack/react-table";
import {
    removeLayersIfExists,
    removeSourcesIfExists,
    invertedTurfBboxToFourCornerCoordinates,
    Coordinates,
} from "@/lib/utils/map";
import { ImageSource } from "mapbox-gl";
import {
    IRRIGATION_SUMMARY_IMAGE_LAYER,
    IRRIGATION_SUMMARY_SOURCE,
} from "@/lib/map/layers";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMap, faTrash } from "@fortawesome/free-solid-svg-icons";
import { Button, Chip } from "@heroui/react";
import { useNavigate } from "@tanstack/react-router";
import TableV2 from "@/components/table/TableV2";

function IrrigationSummary({
    startDate,
    endDate,
}: {
    startDate?: string;
    endDate?: string;
}) {
    const { t } = useTranslation("operations");
    const { formatUnit, formatDateMMDDYYYY } = useI18n();
    const confirm = useConfirm();
    const { deleteOperations } = useFeatureFlag();
    const { fieldId, fieldBbox } = useFieldDashboard() ?? {};
    const { map, mapLoaded } = useMap();
    const { useGetRequest, useMutateRequest } = useRequest();
    const [selectedOperationId, setSelectedOperationId] = useState<
        number | undefined
    >();
    const navigate = useNavigate();

    const operationsQuery = useGetRequest<ListIrrigations>({
        url: `fields/${fieldId}/irrigation_operations/`,
        enabled: !!fieldId,
        params: {
            start_date: startDate,
            end_date: endDate,
        },
    });

    const imageQuery = useGetRequest({
        url: `irrigation_operations/${selectedOperationId}/map_display_image/`,
        responseType: "blob",
        enabled: !!selectedOperationId,
    });

    useEffect(() => {
        if (!map || !mapLoaded || !fieldBbox) return;

        const imageUrl = !imageQuery.data
            ? null
            : window.URL.createObjectURL(imageQuery.data as Blob);
        const bbox = selectedOperationId
            ? (invertedTurfBboxToFourCornerCoordinates(
                  operationsQuery.data?.operations.find(
                      (op) => op.id === selectedOperationId,
                  )?.bounds,
              ) as Coordinates)
            : undefined;
        if (imageUrl) {
            const imSource = map.getSource<ImageSource>(
                IRRIGATION_SUMMARY_SOURCE,
            );
            if (imSource) {
                if (imSource.url !== imageUrl) {
                    imSource.updateImage({
                        url: imageUrl,
                        coordinates: bbox || fieldBbox,
                    });
                }
            } else {
                map.addSource(IRRIGATION_SUMMARY_SOURCE, {
                    type: "image",
                    url: imageUrl,
                    coordinates: bbox || fieldBbox,
                });
                map.addLayer(IRRIGATION_SUMMARY_IMAGE_LAYER);
            }
        } else {
            removeLayersIfExists(map, [IRRIGATION_SUMMARY_IMAGE_LAYER.id]);
            removeSourcesIfExists(map, [IRRIGATION_SUMMARY_SOURCE]);
        }
    }, [
        imageQuery.data,
        fieldBbox,
        map,
        mapLoaded,
        operationsQuery.data,
        selectedOperationId,
    ]);

    const deleteOperationMutation = useMutateRequest({
        url: `irrigation_operations/${selectedOperationId}/`,
        method: "delete",
        errorMessage: t("error.failed_to_delete_operation"),
        successMessage: t("success.operation_deleted_successfully"),
        onSuccess: () => {
            operationsQuery.refetch();
            setSelectedOperationId(undefined);
        },
    });

    const handleDelete = useCallback(() => {
        if (!selectedOperationId || !deleteOperations) return;
        confirm.showConfirm({
            title: t("delete_irrigation_operation"),
            description: t("confirm_delete_irrigation_operation"),
            color: "danger",
            confirmText: t("actions.delete"),
            onConfirm: async () => {
                deleteOperationMutation.mutate({});
            },
        });
    }, [
        selectedOperationId,
        deleteOperations,
        confirm,
        t,
        deleteOperationMutation,
    ]);

    const [sortDescriptor, setSortDescriptor] = useState<SortingState>([
        {
            id: "start_date",
            desc: false,
        },
    ]);

    const totalDepth = useMemo(() => {
        if (!operationsQuery.data) return 0;
        return operationsQuery.data.operations
            .reduce((sum, operation) => sum + (operation.average_depth ?? 0), 0)
            .toFixed(2);
    }, [operationsQuery.data]);

    const columns: ColumnDef<IrrigationOperation>[] = useMemo(() => {
        return [
            {
                accessorKey: "start_date",
                header: t("date"),
                cell: ({ row }) => formatDateMMDDYYYY(row.original.start_date),
            },
            {
                accessorKey: "average_depth",
                header: `${t("depth")} (${formatUnit("in", "cm")})`,
                cell: ({ row }) =>
                    row.original.average_depth
                        ? row.original.average_depth.toFixed(2)
                        : "-",
            },
            {
                accessorKey: "actions",
                header: t("actions"),
                cell: ({ row }) => (
                    <div className="flex flex-row gap-1 items-center w-full justify-center">
                        <Button
                            isIconOnly
                            color="primary"
                            variant={
                                selectedOperationId === row.original.id
                                    ? "solid"
                                    : "bordered"
                            }
                            className="tiny-icon-button"
                            onPress={() => {
                                setSelectedOperationId(
                                    selectedOperationId === row.original.id
                                        ? undefined
                                        : row.original.id,
                                );
                            }}
                            aria-label="View Operation"
                        >
                            <FontAwesomeIcon icon={faMap} />
                        </Button>
                        {deleteOperations && (
                            <Button
                                isIconOnly
                                color="danger"
                                className="tiny-icon-button"
                                onPress={() => {
                                    setSelectedOperationId(row.original.id);
                                    handleDelete();
                                }}
                                aria-label="Delete Operation"
                            >
                                <FontAwesomeIcon icon={faTrash} />
                            </Button>
                        )}
                    </div>
                ),
            },
        ];
    }, [
        t,
        formatDateMMDDYYYY,
        formatUnit,
        selectedOperationId,
        deleteOperations,
        handleDelete,
    ]);

    return (
        <div>
            <div className="flex flex-row justify-between">
                <div className="font-semibold text-lg flex justify-start items-center">
                    {t("irrigation")}
                </div>
                {operationsQuery.data && (
                    <Chip color="primary" variant="flat">
                        {t("total_depth")}: {totalDepth}{" "}
                        {formatUnit("in", "cm")}
                    </Chip>
                )}
            </div>
            <TableV2
                data={operationsQuery.data?.operations || []}
                columns={columns}
                isLoading={operationsQuery.isLoading}
                sorting={sortDescriptor}
                setSorting={setSortDescriptor}
                manualSorting={false}
                onRowClick={(row) => {
                    navigate({
                        to: `/dashboard/fields/${fieldId}/operations/irrigation/${row.id}`,
                    });
                }}
            />
        </div>
    );
}

export default IrrigationSummary;
